import { HttpClient } from '@hot-club-http/client/http-client'
import { Logger } from '@hot-club-http/client/utils'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

import type { HotClubHttpClientProviderParams } from './types'

import './style.css'

const queryClientFallback = HttpClient.createQueryClient()
/**
 * Provides a QueryClient context for managing API requests and caching.
 *
 * This provider ensures that all API calls made using `HotClubHttpClient`
 * are wrapped in a `QueryClientProvider` for React Query state management.
 * It also conditionally renders the React Query DevTools for debugging.
 *
 * @param {object} props - The component props.
 * @param {React.ReactNode} props.children - The child components wrapped by the provider.
 * @param {boolean} [props.activeQueryDevtools=false] - Whether to enable the React Query DevTools.
 * @param {'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'} [props.position='bottom-left']
 *   - The position of the React Query DevTools toggle button.
 *
 * @returns {JSX.Element} A `QueryClientProvider` wrapping the application with optional DevTools.
 *
 * @example
 * // Basic usage
 * <HotClubHttpClientProvider activeQueryDevtools>
 *   <App />
 * </HotClubHttpClientProvider>
 *
 * @example
 * <HotClubHttpClientProvider activeQueryDevtools={true} position="top-right">
 *   <App />
 * </HotClubHttpClientProvider>
 *
 */
const HotClubHttpClientProvider = ({
  queryClient,
  children,
  activeQueryDevtools = false,
  position = 'bottom-left',
  debug = false
}: HotClubHttpClientProviderParams) => {
  debug && Logger.log(`HotClubHttpClientProvider`, { queryClient })

  return (
    <QueryClientProvider client={queryClient || queryClientFallback}>
      {children}
      {activeQueryDevtools && (
        <ReactQueryDevtools
          initialIsOpen={false}
          position={position}
          panelProps={{
            className: 'hot-club-http-provider-panel'
          }}
          toggleButtonProps={{
            className: 'hot-club-http-provider-button'
          }}
        />
      )}
    </QueryClientProvider>
  )
}

export default HotClubHttpClientProvider
